import { animated, useSpring } from '@react-spring/web';
import React, { FunctionComponent, useState } from 'react';
import { useElementSize } from '../../hooks/useElementSize/useElementSize';
import { useSpringReset } from '../../hooks/useSpringReset';
import { prepareProps } from '../../utils/propUtils';
import styles from './ExpandableContainer.module.css';

export type ExpandableContainerProps = {
  expanded: boolean;
  'data-testid'?: string;
};

export const ExpandableContainer: FunctionComponent<ExpandableContainerProps> =
  React.memo((props) => {
    const {
      expanded,
      children,
      'data-testid': dataTestId
    } = prepareProps({ props });

    const [renderChildren, setRenderChildren] = useState(expanded);
    const [contentElement, setContentElement] = useState<HTMLElement | null>(
      null
    );
    const contentElementSize = useElementSize(contentElement);

    const reset = useSpringReset();

    const contentWrapperStyle = useSpring({
      from: { height: 0 },
      to: { height: contentElementSize?.clientHeight || 100 },
      reset,
      config: {
        frequency: 0.5,
        damping: 0.5,
        mass: 0.5,
        clamp: true
      },
      reverse: !expanded,
      onStart: () => {
        if (expanded) {
          setRenderChildren(true);
        }
      },
      onRest: () => {
        if (!expanded) {
          setRenderChildren(false);
        }
      }
    });

    return (
      <animated.div
        className={styles.animationContainer}
        style={contentWrapperStyle}
        data-testid={dataTestId}
      >
        <div
          ref={setContentElement}
          className={styles.contentContainer}
        >
          {renderChildren ? children : null}
        </div>
      </animated.div>
    );
  });
